/**
 * Briques communes des échanges avec les élèves, dans l'écran « Messages » :
 * libellés, styles et états (chargement, erreur, rafraîchissement en échec).
 */
import { Loader2 } from 'lucide-react';
import {
  studentMessagingError,
  type ConversationKind,
} from '../api/studentMessaging';

/** Affiché quand le serveur n'a pas répondu ; sinon son message est repris tel quel. */
export const OFFLINE =
  'Connexion impossible. Vérifiez votre réseau, puis réessayez.';

/**
 * Marque d'une conversation où un élève lit. Elle voisine avec les
 * conversations de l'équipe : chaque libellé nomme donc l'élève.
 */
export const KIND_LABELS: Record<ConversationKind, string> = {
  DIRECT: 'Élève',
  GROUP: 'Groupe avec élèves',
  OFFICE: 'Élève et secrétariat',
};

/** Rappel affiché dans le fil : les fonctions de la messagerie de l'équipe n'y existent pas. */
export const TEXT_ONLY: Record<ConversationKind, string> = {
  DIRECT: 'Conversation avec un élève : texte seul.',
  GROUP: 'Conversation avec des élèves : texte seul.',
  OFFICE: 'Conversation avec un élève : texte seul.',
};

export const primaryButton =
  'inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold shadow-sm transition-colors disabled:opacity-50';
export const secondaryButton =
  'inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-sm font-medium transition-colors disabled:opacity-50';
// 16 px sur téléphone : en dessous, iOS zoome sur le champ à la saisie.
export const field =
  'w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500';
export const card = 'bg-white rounded-2xl border border-slate-200 shadow-sm';

export function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div className="p-6 text-center space-y-3">
      <p role="alert" className="text-sm text-red-600">
        {message}
      </p>
      <button type="button" className={secondaryButton} onClick={onRetry}>
        Réessayer
      </button>
    </div>
  );
}

/** Le contenu reste affiché : seul le dernier rafraîchissement a échoué. */
export function RefreshNotice({
  error,
  onRetry,
}: {
  error: unknown;
  onRetry: () => void;
}) {
  return (
    <div
      role="status"
      className="flex items-center justify-between gap-3 px-4 py-2 bg-amber-50 border-b border-amber-100 text-xs text-amber-800"
    >
      <span>{studentMessagingError(error, OFFLINE)}</span>
      <button
        type="button"
        onClick={onRetry}
        className="flex-shrink-0 font-semibold underline"
      >
        Réessayer
      </button>
    </div>
  );
}

export function Loading({ label }: { label: string }) {
  return (
    <p className="flex items-center justify-center gap-2 p-6 text-sm text-slate-500">
      <Loader2 size={16} className="animate-spin" />
      {label}
    </p>
  );
}
